import { AuthService, ConfigStateService, LocalizationService, ReplaceableComponentsService } from '@abp/ng.core';
import { Component, Inject, inject, OnInit, HostListener } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { slideAnimation } from './shared/animations/route-animations';
import { filter, firstValueFrom, take, switchMap, of, from } from 'rxjs';
import { LoginComponent } from './login/login.component';
import { eAccountComponents } from '@abp/ng.account';
import { eThemeLeptonXComponents } from '@abp/ng.theme.lepton-x';
import { APP_ICONS } from './shared/icons/app-icons';
import { DOCUMENT } from '@angular/common';
import { OAuthService } from 'angular-oauth2-oidc';
import { MetroHeaderItem } from './shared/components/metro-header/metro-header.component';
import { PageTitleService } from './shared/services/page-title.service';
import { MenuService } from './shared/services/menu.service';
import { MetroNavItem } from './shared/components/metro-nav-menu/metro-nav-menu.component';
import { SommelierService } from '@proxy/sommeliers';
import { SelectedSommelierService } from './shared/services/selected-sommelier.service';
import { ClientService } from '@proxy/clients';
import { ForgotPasswordCustomComponent } from './forgot-password/forgot-password.component';
import { ResetPasswordCustomComponent } from './reset-password/reset-password.component';
import { eSettingManagementComponents } from '@abp/ng.setting-management';
import { CustomSettingsComponent } from './setting-management/settings.component';
import { CommunicationService } from './shared/services/comunication.service';
import { CartStateService } from './shared/services/cart-state.service';
import { AppUpdateService } from './shared/services/app-update.service';
import { MetroToastService } from './shared/services/metro-toast.service';
import { BiometricAuthService } from './shared/services/biometric-auth.service';

const ICON_USER = APP_ICONS.user;

export const HEADER_ITEMS: MetroHeaderItem[] = [
  {
    iconSvg: ICON_USER,
    children: [
      { label: '::Logout', path: 'logout' }
    ]
  },
  {
    iconSvg: APP_ICONS.cart,
    path: ''
  }
]
@Component({
  standalone: false,
  selector: 'app-root',
  templateUrl: './app.component.html',
  animations: [slideAnimation]
})
export class AppComponent implements OnInit {
  private authService = inject(AuthService);
  private config = inject(ConfigStateService);

  public navItems: MetroNavItem[] = [];
  public navItemsMobile: MetroNavItem[] = [];

  public headerItems = HEADER_ITEMS;
  public currentRoute: string | null = null;
  public routeAnimationState: string | number = 0;

  isAdmin: boolean = false;
  pageTitle = '';
  userName = ''; // Property to store current user name

  cartCount = 0;
  isLoginPage = false;
  isProcessingExternalLogin = false;
  biometricLocked = false;

  private localizationService = inject(LocalizationService);

  constructor(
    @Inject(DOCUMENT) private doc: Document,
    public router: Router,
    private replaceableComponents: ReplaceableComponentsService,
    private oauth: OAuthService,
    private activatedRoute: ActivatedRoute,
    private pageTitleService: PageTitleService,
    private menuService: MenuService,
    private sommelierService: SommelierService,
    private clientService: ClientService,
    private selectedSommelierService: SelectedSommelierService,
    private communicationService: CommunicationService,
    private cartStateService: CartStateService,
    private toastService: MetroToastService,
    private appUpdate: AppUpdateService,
    private biometric: BiometricAuthService

  ) {
    this.pageTitleService.title$.subscribe(title => {
      this.pageTitle = title;
    });

    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: NavigationEnd) => {
      const current = this.getChild(this.activatedRoute);
      const routeTitle = current.snapshot.data['title'] || '';
      this.pageTitleService.setTitle(routeTitle);

      const urlPath = event.urlAfterRedirects.split('?')[0].split('#')[0];
      const publicRoutes = ['/account/login', '/account/confirm-email', '/account/forgot-password'];
      this.isLoginPage = urlPath === '/' || publicRoutes.some(r => urlPath.startsWith(r));

      this.updateAnimationState(urlPath);
    });

    this.replaceableComponents.add({
      component: LoginComponent,
      key: eAccountComponents.Login,
    });

    this.replaceableComponents.add({
      component: LoginComponent,
      key: eThemeLeptonXComponents.Login,
    });

    this.replaceableComponents.add({
      component: ForgotPasswordCustomComponent,
      key: eAccountComponents.ForgotPassword,
    });

    this.replaceableComponents.add({
      component: ResetPasswordCustomComponent,
      key: eAccountComponents.ResetPassword,
    });

    this.replaceableComponents.add({
      component: CustomSettingsComponent,
      key: eSettingManagementComponents.SettingManagement,
    });
  }

  updateAnimationState(urlPath: string) {
    const currentPath = urlPath.endsWith('/') ? urlPath.slice(0, -1) : urlPath;

    const items = (this.navItemsMobile && this.navItemsMobile.length > 0)
      ? this.navItemsMobile
      : this.navItems;

    if (!items || items.length === 0) {
      this.routeAnimationState = urlPath;
      return;
    }

    let bestIndex = -1;
    let maxLength = -1;

    items.forEach((item, index) => {
      if (!item.path || item.path === '#') return;
      const itemPath = item.path.endsWith('/') ? item.path.slice(0, -1) : item.path;

      if (currentPath === itemPath || currentPath.startsWith(itemPath + '/')) {
        if (itemPath.length > maxLength) {
          maxLength = itemPath.length;
          bestIndex = index;
        }
      }
    });

    this.routeAnimationState = bestIndex >= 0 ? bestIndex : urlPath;
  }

  onNavigate(path: string) {
    this.router.navigate([path]).catch(err => {
      console.error(`Error navigating to ${path}`, err);
    });
  }

  @HostListener('document:visibilitychange', ['$event'])
  async onVisibilityChange(event: Event) {
    if (this.doc.visibilityState === 'visible') {
      if (!this.oauth.hasValidAccessToken() && this.oauth.getRefreshToken()) {
        if (this.biometric.isSupported() && this.biometric.hasCredential() && !this.biometricLocked) {
          this.biometricLocked = true;
          return;
        }
        try {
          await this.oauth.refreshToken();
        } catch (error) {
          // no se pudo refrescar
        }
      }
    }
  }

  /**
   * Escucha eventos de expiración de token del OAuth para mostrar la pantalla de biometría
   * en lugar de redirigir al login.
   */
  private setupTokenExpirationListener(): void {
    this.oauth.events.subscribe(async (event: any) => {
      if (event.type === 'token_received') {
        this.scheduleProactiveRefresh();
        const refreshToken = this.oauth.getRefreshToken();
        if (refreshToken) {
          localStorage.setItem('wp_refresh_token', refreshToken);
        }
      }

      const hasBiometricSession = localStorage.getItem('wp_biometric_session_active') === 'true';
      const persistedRefreshToken = localStorage.getItem('wp_refresh_token');
      const hasRefreshToken = !!this.oauth.getRefreshToken() || !!persistedRefreshToken;

      const shouldShowBiometricLock = 
        !this.oauth.hasValidAccessToken() &&
        this.biometric.isSupported() &&
        this.biometric.hasCredential() &&
        !this.biometricLocked &&
        hasRefreshToken;

      if ((event.type === 'token_expires' || event.type === 'session_terminated' || event.type === 'token_refresh_error')
          && shouldShowBiometricLock) {
        this.biometricLocked = true;
      }
    });
  }

  /**
   * Programa un refresh del token 10 segundos ANTES de que expire.
   * Así evitamos que el auto-refresh del library falle y borre el refresh token.
   */
  private proactiveRefreshTimer: any;
  private scheduleProactiveRefresh(): void {
    if (this.proactiveRefreshTimer) {
      clearTimeout(this.proactiveRefreshTimer);
    }
    const expiresAt = this.oauth.getAccessTokenExpiration();
    if (!expiresAt) return;

    const now = Date.now();
    const expiresMs = expiresAt;
    const refreshInMs = Math.max((expiresMs - now) - 10000, 5000);

    this.proactiveRefreshTimer = setTimeout(async () => {
      try {
        await this.oauth.refreshToken();
      } catch (e: any) {
        if (this.biometric.isSupported() && this.biometric.hasCredential() && !this.biometricLocked) {
          this.biometricLocked = true;
        }
      }
    }, refreshInMs);
  }

  get hasLoggedIn(): boolean {
    return this.authService.isAuthenticated;
  }

  private async configureNavigationByRolesAsync(): Promise<void> {
    const currentUser = await firstValueFrom(this.config.getOne$('currentUser'));
    const roles = currentUser?.roles ?? [];
    this.isAdmin = roles.includes('admin');

    // Clone HEADER_ITEMS and filter out cart if admin
    this.headerItems = HEADER_ITEMS
      .filter(item => !(item.iconSvg === APP_ICONS.cart && this.isAdmin))
      .map(item => ({ ...item })); // Shallow clone items to avoid modifying the constant

    this.userName = currentUser?.name || currentUser?.userName || '';
    const userItem = this.headerItems.find(item => item.iconSvg === ICON_USER);
    if (userItem) {
      userItem.label = this.userName;
    }

    this.cartStateService.loadInitialCount(currentUser.id)
    this.cartStateService.itemCount$.subscribe(count => {
      this.cartCount = count;
    });

    let slug;
    if (roles.includes('sommelier')) {
      slug = await firstValueFrom(this.sommelierService.getMySlug());
    } else if (roles.includes('client')) {
      slug = await firstValueFrom(this.clientService.getSommelierSlug());
    }

    if (slug) {
      this.selectedSommelierService.setSlug(slug);
      const cartItem = this.headerItems.find(i => i.iconSvg === APP_ICONS.cart);
      if (cartItem) {
        cartItem.iconName = "cart"
        cartItem.path = `/${slug}/shopping-cart`;
      }
    }
  }


  async ngOnInit() {
    // Manejar errores del login externo
    const hash = this.doc.location.hash;
    if (hash.includes('external=user_not_found')) {
      const params = new URLSearchParams(hash.substring(1));
      const email = params.get('email') || '';
      const errorMessage = email
        ? this.localizationService.instant('::ExternalLogin:UserNotFoundWithEmail', email)
        : this.localizationService.instant('::ExternalLogin:UserNotFound');

      this.toastService.error(
        errorMessage,
        this.localizationService.instant('::ExternalLogin:ErrorTitle'),
        { life: 7000 }
      );

      // Limpiar el hash y navegar al login
      this.doc.location.hash = '';
      await this.router.navigate(['/account/login']);
      // sin return: seguir con el resto de ngOnInit (conexión, listeners, quitar el splash)
    }

    // winepassport#398 — el usuario canceló en Google: volver al login sin error
    if (hash.includes('external=cancelled')) {
      this.doc.location.hash = '';
      await this.router.navigate(['/account/login']);
      // sin return: seguir con el resto de ngOnInit (conexión, listeners, quitar el splash)
    }

    if (hash.includes('external=error')) {
      this.toastService.error(
        this.localizationService.instant('::ExternalLogin:Error'),
        this.localizationService.instant('::ExternalLogin:ErrorTitle'),
        { life: 7000 }
      );
      this.doc.location.hash = '';
      await this.router.navigate(['/account/login']);
      // sin return: seguir con el resto de ngOnInit (conexión, listeners, quitar el splash)
    }

    if (hash.includes('external=account_inactive')) {
      this.toastService.error(
        this.localizationService.instant('::ExternalLogin:AccountInactive'),
        this.localizationService.instant('::ExternalLogin:AccountInactiveTitle'),
        { life: 7000 }
      );
      this.doc.location.hash = '';
      await this.router.navigate(['/account/login']);
      // sin return: seguir con el resto de ngOnInit (conexión, listeners, quitar el splash)
    }

    if (hash.includes('external=missing_id')) {
      this.toastService.error(
        this.localizationService.instant('::ExternalLogin:MissingId'),
        this.localizationService.instant('::ExternalLogin:ErrorTitle'),
        { life: 7000 }
      );
      this.doc.location.hash = '';
      await this.router.navigate(['/account/login']);
      // sin return: seguir con el resto de ngOnInit (conexión, listeners, quitar el splash)
    }

    this.config.getOne$('currentUser')
      .pipe(filter(u => !!u))
      .subscribe(async user => {
        await this.configureNavigationByRolesAsync();
      });

    this.menuService.getNavItems().subscribe(items => {
      this.navItems = items;
      this.updateAnimationState(this.router.url.split('?')[0].split('#')[0]);
    });
    this.menuService.getMobileNavItems().subscribe(items => {
      this.navItemsMobile = items;
      this.updateAnimationState(this.router.url.split('?')[0].split('#')[0]);
    });

    // Verificar si acabamos de completar un login externo
    // Esperar a que el usuario esté autenticado y tenga roles antes de redirigir
    const hasExternalLoginPending = sessionStorage.getItem('externalLoginPending') === 'true';
    if (hasExternalLoginPending && this.authService.isAuthenticated) {
      // Marcar que estamos procesando para ocultar el contenido
      this.isProcessingExternalLogin = true;
      // Esperar a que los tokens estén disponibles y el usuario tenga roles
      // Luego navegar a '/' para que el guard maneje la redirección
      this.handlePostExternalLoginFlow();
    }

    this.communicationService.connect();

    // Escuchar expiración de token para mostrar lock biométrico
    this.setupTokenExpirationListener();

    // Escuchar cuando el login biométrico se complete para cerrar el lock screen
    this.communicationService.biometricLoginComplete$.subscribe(() => {
      this.biometricLocked = false;
    });

    // Cold-start: si el token está expirado pero hay biometría registrada, mostrar lock screen
    const hasBiometricSession = localStorage.getItem('wp_biometric_session_active') === 'true';
    if (!this.oauth.hasValidAccessToken()
        && this.biometric.isSupported()
        && this.biometric.hasCredential()
        && (this.oauth.getRefreshToken() || hasBiometricSession)) {
      this.biometricLocked = true;
    }

    if (this.appUpdate['updates'].isEnabled) {
      this.appUpdate['updates'].checkForUpdate();
    }

    // Fix para el modal de ABP Identity - Permitir cerrar con Escape o clic en overlay
    this.setupModalCloseHandlers();

    // Remover splash screen SOLO si no es móvil o no hay biometría registrada o el usuario ya está autenticado
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(this.doc.defaultView?.navigator?.userAgent || '');
    const isLoginPage = this.router.url === '/' || this.router.url.startsWith('/account/login');
    const hasBiometric = this.biometric.isSupported() && this.biometric.hasCredential();
    const isNotAuthenticated = !this.authService.isAuthenticated;

    if (isMobile && isLoginPage && hasBiometric && isNotAuthenticated) {
      // Mantener splash activo para biometría en móvil solo si el usuario NO está autenticado
      setTimeout(() => {
        if (this.doc.body.classList.contains('splash-active')) {
          this.doc.body.classList.remove('splash-active');
        }
      }, 5000);
    } else {
      this.doc.body.classList.remove('splash-active');
    }
  }

  private setupModalCloseHandlers(): void {
    // Listener para cerrar modal con Escape
    this.doc.addEventListener('keydown', (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        const modal = this.doc.querySelector('.modal.show, .offcanvas.show') as HTMLElement;
        if (modal) {
          // Intentar encontrar y hacer clic en el botón de cerrar
          const closeButton = modal.querySelector('.btn-close, button[data-bs-dismiss="modal"], button[data-bs-dismiss="offcanvas"]') as HTMLElement;
          if (closeButton) {
            event.preventDefault();
            event.stopPropagation();
            closeButton.click();
            return;
          }

          // Si no hay botón de cerrar, buscar botón Cancelar
          const cancelButton = Array.from(modal.querySelectorAll('button.btn-secondary, button[type="button"]'))
            .find(btn => {
              const text = (btn as HTMLElement).textContent?.trim().toLowerCase() || '';
              return text.includes('cancel') || text.includes('cancelar');
            }) as HTMLElement;

          if (cancelButton) {
            event.preventDefault();
            event.stopPropagation();
            cancelButton.click();
          }
        }
      }
    });

    // Listener para cerrar modal con clic en overlay (usando delegación de eventos)
    this.doc.addEventListener('click', (event: MouseEvent) => {
      const target = event.target as HTMLElement;

      // Verificar si el clic es en el backdrop
      if (target.classList.contains('modal-backdrop') ||
        target.classList.contains('offcanvas-backdrop') ||
        (target.classList.contains('modal') && target === event.target)) {
        const modal = this.doc.querySelector('.modal.show, .offcanvas.show') as HTMLElement;
        if (modal) {
          // Buscar botón de cerrar o cancelar
          const closeButton = modal.querySelector('.btn-close, button[data-bs-dismiss="modal"], button[data-bs-dismiss="offcanvas"]') as HTMLElement;
          if (closeButton) {
            event.preventDefault();
            event.stopPropagation();
            closeButton.click();
            return;
          }

          const cancelButton = Array.from(modal.querySelectorAll('button.btn-secondary, button[type="button"]'))
            .find(btn => {
              const text = (btn as HTMLElement).textContent?.trim().toLowerCase() || '';
              return text.includes('cancel') || text.includes('cancelar');
            }) as HTMLElement;

          if (cancelButton) {
            event.preventDefault();
            event.stopPropagation();
            cancelButton.click();
          }
        }
      }
    }, true); // Usar capture phase para capturar antes que otros handlers
  }

  /**
   * Maneja el flujo después de completar el login externo
   * Replica la lógica del guard routeRedirectCanActivate para redirigir directamente
   */
  private handlePostExternalLoginFlow(): void {
    // Esperar a que el usuario tenga roles y luego redirigir directamente
    this.config.getOne$('currentUser')
      .pipe(
        filter(u => !!u),
        take(1),
        switchMap(user => {
          const roles = (user as any)?.roles as string[] | undefined;

          // Si no tiene roles, forzar refresh
          if (!roles || roles.length === 0) {
            return this.config.refreshAppState().pipe(
              switchMap(() => this.config.getOne$('currentUser').pipe(
                filter(u => !!u && Array.isArray((u as any).roles) && (u as any).roles.length > 0),
                take(1)
              ))
            );
          }

          return of(user as any);
        }),
        switchMap(userWithRoles => {
          const roles = (userWithRoles as any)?.roles ?? [];

          // Resolver el slug según el rol
          if (roles.includes('sommelier')) {
            return from(firstValueFrom(this.sommelierService.getMySlug())).pipe(
              switchMap(slug => of({ slug, roles }))
            );
          }
          if (roles.includes('client')) {
            return from(firstValueFrom(this.clientService.getSommelierSlug())).pipe(
              switchMap(slug => of({ slug, roles }))
            );
          }

          return of({ slug: undefined, roles });
        }),
        take(1)
      )
      .subscribe(async ({ slug }) => {
        try {
          sessionStorage.removeItem('externalLoginPending');

          if (slug) {
            // Navegar directamente al perfil del usuario
            await this.router.navigate([`/${slug}`], { replaceUrl: true });
          } else {
            // Si no hay slug, navegar al home
            await this.router.navigate(['/'], { replaceUrl: true });
          }

          // Ocultar el loader después de navegar
          this.isProcessingExternalLogin = false;
        } catch (error) {
          console.error('Error en handlePostExternalLoginFlow:', error);
          sessionStorage.removeItem('externalLoginPending');
          this.isProcessingExternalLogin = false;
        }
      });
  }

  async unlockWithBiometric() {
    const ok = await this.biometric.authenticate();
    if (ok) {
      this.biometricLocked = false;
      let refreshToken = this.oauth.getRefreshToken();
      if (!refreshToken) {
        refreshToken = localStorage.getItem('wp_refresh_token');
        if (refreshToken) {
          (this.oauth as any)._storage.setItem('refresh_token', refreshToken);
        }
      }

      if (!this.oauth.hasValidAccessToken() && refreshToken) {
        try {
          await this.oauth.refreshToken();
          await firstValueFrom(this.config.refreshAppState());
        } catch (e) {
          // no se pudo refrescar tras desbloqueo biométrico
        }
      }
    }
  }

  logoutFromBiometricLock() {
    if (this.biometric.hasCredential()) {
      this.oauth.logOut(false);
      this.router.navigate(['/account/login'], { replaceUrl: true });
    } else {
      localStorage.removeItem('wp_biometric_session_active');
      localStorage.removeItem('wp_refresh_token');
      this.authService.logout().subscribe();
    }
  }

  onHeaderAction(item: MetroHeaderItem) {
    if (item.path === 'logout') {
      if (this.biometric.hasCredential()) {
        this.selectedSommelierService.clearSlug();
        this.oauth.logOut(false);
        this.router.navigate(['/account/login'], { replaceUrl: true });
      } else {
        localStorage.removeItem('wp_biometric_session_active');
        localStorage.removeItem('wp_refresh_token');
        this.authService.logout().subscribe({
          next: () => {
            this.selectedSommelierService.clearSlug();
          },
          error: (err) => {
            console.error('Error al cerrar sesión', err);
          }
        });
      }
    }
  }

  private getChild(route: ActivatedRoute): ActivatedRoute {
    if (route.firstChild) {
      return this.getChild(route.firstChild);
    }
    return route;
  }

}
